<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import ImovelAPI from 'dashboard/api/imoveis';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import { trackCrmEvent } from '../utils/crmTracking';
import { useVisitorInfo } from '../composables/useVisitorInfo';
import {
  activeBusinessContextFromLead,
  houseAddressLine,
  houseDetailsLine,
  houseId,
  isAgentVisit,
  isRentVisitorContext,
  listingUrl,
  normalizeVisitorBusinessContext,
  offerDeepLinkUrl,
  priceRangeLabel,
  visitDateTimeLabel,
  visitDetailsUrl,
  visitHouse,
  visitorAttendanceStageLabel,
  visitorClientSinceLabel,
  visitorOfferAmountLabel,
  visitorOfferDetailUrl,
  visitorOfferHouse,
  visitorOfferId,
  visitorOfferPaymentLabel,
  visitorOfferReference,
  visitorOfferStatusLabel,
  visitorOfferSubmittedAtLabel,
  visitorPhone,
  visitorProfileTags,
} from '../utils/visitorInfoPresenter';
import VisitorInfoSection from './VisitorInfoSection.vue';
import VisitListCard from './VisitListCard.vue';
import VisitorProfileHouseSection from './VisitorProfileHouseSection.vue';
import VisitorProfileHeader from './VisitorProfileHeader.vue';
import VisitorSuggestedHouses from './VisitorSuggestedHouses.vue';

const props = defineProps({
  visitorId: { type: [String, Number], default: '' },
  businessContext: { type: String, default: 'for_rent' },
  actionContext: { type: Object, default: () => ({}) },
  lead: { type: Object, default: null },
  embedded: { type: Boolean, default: false },
});

const emit = defineEmits([
  'phone',
  'chat',
  'scheduleVisit',
  'openVisit',
  'sendOffer',
  'viewListing',
]);

const VISITOR_PROFILE_EXTRA_COPY = Object.freeze({
  clientProfileTabsLabel: 'ClientProfile',
  tabOverview: 'Resumo',
  tabHouses: 'Imóveis',
  tabNotes: 'Notas',
  tabHistory: 'Histórico',
  summaryTitle: 'Resumo do relacionamento',
  summaryEmpty: 'Nenhum dado de resumo disponível para este visitante.',
  preferencesTitle: 'Preferências declaradas',
  preferencesEmpty: 'Nenhuma preferência declarada para este perfil.',
  journeyTitle: 'Jornada do visitante',
  activeOfferTitle: 'Proposta ativa',
  activeOfferOpen: 'Abrir proposta',
  activeOfferListing: 'Abrir imóvel relacionado',
  activeOfferEmpty: 'Sem proposta ativa carregada.',
  housesTitle: 'Imóveis do perfil',
  favoriteHousesTitle: 'Imóveis favoritos',
  favoriteHousesEmpty: 'Nenhum imóvel favoritado por este visitante.',
  visitedHousesTitle: 'Casas visitadas',
  visitedHousesEmpty: 'Nenhuma casa visitada registrada.',
  favoritedAtPrefix: 'favoritado em',
  visitedAtPrefix: 'visitado em',
  viewListingAction: 'Abrir imóvel',
  housesEmpty: 'Nenhum imóvel disponível para este perfil.',
  housesAction: 'Nova visita',
  historyEmpty: 'Nenhuma visita finalizada ou cancelada para este visitante.',
  offersTitle: 'Propostas em andamento',
  offersHelper:
    'Use esse bloco para ligar visita, perfil e fluxo de proposta sem sair da jornada do cliente.',
  offersEmpty: 'Nenhuma proposta em andamento para este visitante.',
  offersLoading: 'Carregando propostas em andamento',
  offersLoadMore: 'Carregar mais propostas',
  offerReferencePrefix: 'Ref.',
  offerSentAtPrefix: 'enviada em',
  offerSeparator: '·',
  offerFallbackHouse: 'Imóvel da proposta',
  offerFallbackAmount: 'Valor não informado',
  viewOffers: 'Ver propostas',
  newVisit: 'Nova visita',
  detailedHistoryTitle: 'Histórico detalhado',
  historyLoadMore: 'Carregar mais',
  historyLoadingMore: 'Carregando histórico',
  notesCardTitle: 'Anotações sobre o cliente',
  notesDescription:
    'Campo destinado a registrar informações sobre o andamento do atendimento',
  noteCreationOrder: 'Ordenado por data de criação',
  noteEmptyState: 'Sem anotações existentes',
  notesLoadMore: 'Carregar mais notas',
  newNote: 'Nova nota',
  newNoteDialogTitle: 'Nova anotação',
  inputPlaceholder: 'Digite uma anotação sobre o cliente',
  saveNoteButton: 'Salvar nota',
  discardNoteTitle: 'Descartar anotação?',
  discardNoteDescription:
    'A anotação ainda não foi salva. Se sair agora, o texto digitado será perdido.',
  discardNoteReturn: 'Continuar editando',
  discardNoteCancel: 'Descartar',
  createNoteSuccess: 'Anotação criada com sucesso.',
  createNoteError: 'Não foi possível salvar a anotação.',
  notesFetchError: 'Não foi possível carregar as anotações do cliente.',
  noteLimitHelper: 'Limite de 255 caracteres.',
  loadMore: 'Carregar mais',
  permissionState: 'Você não tem permissão para ver estes dados.',
  summaryError: 'Não foi possível carregar o resumo do visitante.',
  housesError: 'Não foi possível carregar os imóveis do perfil.',
  suggestionsError: 'Não foi possível carregar as sugestões de imóveis.',
  offersError: 'Não foi possível carregar as propostas em andamento.',
  historyError: 'Não foi possível carregar o histórico operacional.',
  notesPermission: 'Você não tem permissão para ver as notas deste cliente.',
  notesError: 'Não foi possível carregar as notas deste cliente.',
  operationalHistoryTitle: 'Histórico operacional',
  operationalHistoryHelper:
    'Resumo dos registros carregados para orientar o próximo passo.',
});

const VISITOR_PROFILE_PAGE_SIZE = 4;
const VISITOR_NOTES_PAGE_SIZE = 5;

const CLIENT_PROFILE_TABS = Object.freeze({
  OVERVIEW: 'overview',
  HOUSES: 'houses',
  NOTES: 'notes',
  HISTORY: 'history',
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const contactNotesUiFlags = useMapGetter('contactNotes/getUIFlags');

const {
  domainData,
  requestsState,
  errors,
  load,
  loadMoreVisitHistory,
  reset: resetDomainData,
} = useVisitorInfo();

const selectedEntryHouse = ref(null);
const entryInformation = ref(null);
const isLoadingEntryInformation = ref(false);
const isNewNoteDialogOpen = ref(false);
const isDiscardNoteDialogOpen = ref(false);
const currentClientProfileTab = ref(CLIENT_PROFILE_TABS.OVERVIEW);
const noteDraft = ref('');
const favoriteHousesVisibleLimit = ref(VISITOR_PROFILE_PAGE_SIZE);
const visitedHousesVisibleLimit = ref(VISITOR_PROFILE_PAGE_SIZE);
const suggestedHousesVisibleLimit = ref(VISITOR_PROFILE_PAGE_SIZE);
const offersVisibleLimit = ref(VISITOR_PROFILE_PAGE_SIZE);
const notesVisibleLimit = ref(VISITOR_NOTES_PAGE_SIZE);
const notesError = ref('');

const activeBusinessContext = computed(() =>
  props.lead
    ? activeBusinessContextFromLead(props.lead)
    : normalizeVisitorBusinessContext(props.businessContext)
);
const visitorInfo = computed(() => domainData.visitorInfo || {});
const visitorAdditionalAttributes = computed(
  () =>
    visitorInfo.value.additionalAttributes ||
    visitorInfo.value.additional_attributes ||
    {}
);
const leadAdditionalAttributes = computed(
  () =>
    props.lead?.additionalAttributes || props.lead?.additional_attributes || {}
);
const profileSources = computed(() => [
  visitorInfo.value,
  visitorInfo.value.buyerProfile || visitorInfo.value.buyer_profile || {},
  visitorAdditionalAttributes.value.buyerProfile ||
    visitorAdditionalAttributes.value.buyer_profile ||
    {},
  visitorAdditionalAttributes.value,
  props.lead || {},
  props.lead?.buyerProfile || props.lead?.buyer_profile || {},
  leadAdditionalAttributes.value,
]);
const profileValue = (...keys) => {
  const value = profileSources.value
    .filter(Boolean)
    .flatMap(source => keys.map(key => source[key]))
    .find(item => item !== undefined && item !== null && item !== '');

  return value === undefined ? '' : value;
};
const textValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => textValue(item))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (value === undefined || value === null) return '';
  return String(value).trim();
};
const preferenceNumberLabel = (value, suffix = '') => {
  const number = Number(value);
  if (!Number.isFinite(number)) return textValue(value);
  return `${number}${suffix}`;
};
const paymentPreferenceLabel = value => {
  const raw = String(value || '').toLowerCase();
  return (
    {
      cash: 'À vista',
      a_vista: 'À vista',
      à_vista: 'À vista',
      financed: 'Financiamento',
      financiamento: 'Financiamento',
      a_vista_fgts: 'À vista + FGTS',
      financiamento_fgts: 'Financiamento + FGTS',
    }[raw] || textValue(value)
  );
};
const tabStateMessage = (error, fallback) => {
  if (!error) return '';
  if (error === 'permission') return VISITOR_PROFILE_EXTRA_COPY.permissionState;
  return fallback;
};
const notesErrorMessage = computed(() => {
  if (!notesError.value) return '';
  if (notesError.value === 'permission') {
    return VISITOR_PROFILE_EXTRA_COPY.notesPermission;
  }
  return VISITOR_PROFILE_EXTRA_COPY.notesError;
});
const summaryStateMessage = computed(() =>
  tabStateMessage(errors.visitorInfo, VISITOR_PROFILE_EXTRA_COPY.summaryError)
);
const housesStateMessage = computed(
  () =>
    tabStateMessage(errors.houses, VISITOR_PROFILE_EXTRA_COPY.housesError) ||
    tabStateMessage(
      errors.suggestedHouses,
      VISITOR_PROFILE_EXTRA_COPY.suggestionsError
    )
);
const offersStateMessage = computed(() =>
  tabStateMessage(errors.ongoingOffers, VISITOR_PROFILE_EXTRA_COPY.offersError)
);
const historyStateMessage = computed(() =>
  tabStateMessage(errors.visitHistory, VISITOR_PROFILE_EXTRA_COPY.historyError)
);
const summaryRows = computed(() =>
  [
    {
      key: 'stage',
      label: 'Etapa',
      value:
        visitorAttendanceStageLabel(visitorInfo.value) ||
        textValue(
          profileValue('attendance_stage_label', 'attendanceStageLabel')
        ),
    },
    {
      key: 'since',
      label: 'Cliente desde',
      value: visitorClientSinceLabel(visitorInfo.value),
    },
    {
      key: 'phone',
      label: 'Telefone',
      value: visitorPhone(visitorInfo.value),
    },
    {
      key: 'conversation',
      label: 'Última conversa',
      value: visitorInfo.value.latest_conversation_id
        ? `#${visitorInfo.value.latest_conversation_id}`
        : '',
    },
    {
      key: 'context',
      label: 'Contexto',
      value: activeBusinessContext.value === 'for_sale' ? 'Compra' : 'Aluguel',
    },
  ].filter(row => row.value)
);
const preferenceRows = computed(() =>
  [
    {
      key: 'budget',
      label: 'Faixa de preço',
      value: priceRangeLabel(visitorInfo.value),
    },
    {
      key: 'payment',
      label: 'Pagamento',
      value: paymentPreferenceLabel(
        profileValue('payment_type', 'paymentType', 'forma_pagamento')
      ),
    },
    {
      key: 'regions',
      label: 'Regiões',
      value: textValue(
        profileValue(
          'regions',
          'regioes',
          'neighborhoods',
          'neighbourhoods',
          'bairros',
          'preferred_regions'
        )
      ),
    },
    {
      key: 'bedrooms',
      label: 'Quartos',
      value: preferenceNumberLabel(
        profileValue('bedrooms', 'quartos', 'number_of_bedrooms')
      ),
    },
    {
      key: 'parking',
      label: 'Vagas',
      value: preferenceNumberLabel(
        profileValue('parking_spots', 'vagas', 'number_of_parking_spaces')
      ),
    },
    {
      key: 'area',
      label: 'Área',
      value: preferenceNumberLabel(
        profileValue('area', 'area_min', 'total_area'),
        ' m²'
      ),
    },
    {
      key: 'tags',
      label: 'Sinais',
      value: visitorProfileTags(visitorInfo.value, props.lead).join(', '),
    },
  ].filter(row => row.value)
);
const hasLoadedDomainData = computed(
  () =>
    Boolean(domainData.visitorInfo) ||
    Boolean(domainData.suggestedHouses.length) ||
    Boolean(domainData.favoriteHouses.length) ||
    Boolean(domainData.visitedHouses.length) ||
    Boolean(domainData.nextVisitsWithMe.length) ||
    Boolean(domainData.lastVisits.length) ||
    Boolean(domainData.visitHistory.length) ||
    Boolean(domainData.ongoingOffers.length)
);
const hasAnyLoading = computed(() =>
  Object.values(requestsState).some(loading => Boolean(loading))
);
const expectedVisitorInfoErrorKeys = computed(() => [
  'visitorInfo',
  ...(isRentVisitorContext(activeBusinessContext.value)
    ? ['suggestedHouses']
    : []),
  'houses',
  'nextVisitsWithMe',
  'lastVisits',
  'visitHistory',
  'ongoingOffers',
]);
const hasAnyError = computed(() =>
  Object.values(errors).some(error => Boolean(error))
);
const hasTotalError = computed(
  () =>
    !hasAnyLoading.value &&
    !hasLoadedDomainData.value &&
    expectedVisitorInfoErrorKeys.value.every(key => Boolean(errors[key]))
);
const hasPartialError = computed(
  () => hasAnyError.value && !hasTotalError.value
);
const entryRows = computed(() => {
  const info = entryInformation.value || {};
  return [
    {
      key: 'access',
      label: t('LEADS.VISITOR_INFO.ENTRY.ACCESS'),
      value: info.access_label,
    },
    {
      key: 'helper',
      label: t('LEADS.VISITOR_INFO.ENTRY.HELPER'),
      value: info.helper,
    },
    {
      key: 'responsible',
      label: t('LEADS.VISITOR_INFO.ENTRY.RESPONSIBLE'),
      value: info.responsible_name,
    },
    {
      key: 'phone',
      label: t('LEADS.VISITOR_INFO.ENTRY.PHONE'),
      value: info.responsible_phone,
    },
    {
      key: 'address',
      label: t('LEADS.VISITOR_INFO.ENTRY.ADDRESS'),
      value: info.responsible_address,
    },
    {
      key: 'instructions',
      label: t('LEADS.VISITOR_INFO.ENTRY.INSTRUCTIONS'),
      value: info.instructions,
    },
  ].filter(row => row.value);
});
const visitorOffersInProgress = computed(() => domainData.ongoingOffers || []);
const visibleVisitorOffersInProgress = computed(() =>
  visitorOffersInProgress.value.slice(0, offersVisibleLimit.value)
);
const hasMoreVisitorOffers = computed(
  () =>
    visitorOffersInProgress.value.length >
    visibleVisitorOffersInProgress.value.length
);
const activeVisitorOffer = computed(
  () =>
    visitorOffersInProgress.value.find(offer => visitorOfferId(offer)) || null
);
const activeVisitorOfferHouse = computed(() =>
  activeVisitorOffer.value ? visitorOfferHouse(activeVisitorOffer.value) : {}
);
const activeVisitorOfferHouseDescription = computed(() =>
  [
    houseAddressLine(activeVisitorOfferHouse.value),
    houseDetailsLine(activeVisitorOfferHouse.value),
  ]
    .filter(Boolean)
    .join(' · ')
);
const visitorOffersInProgressCount = computed(
  () =>
    visitorOffersInProgress.value.length ||
    visitorInfo.value.proposalsInProgressQuantity ||
    visitorInfo.value.proposals_in_progress_quantity ||
    0
);
const visitorJourneyCards = computed(() => [
  {
    key: 'visits',
    label: 'Visitas realizadas',
    value:
      visitorInfo.value.visitsQuantity ||
      visitorInfo.value.visits_quantity ||
      domainData.visitHistory.length ||
      domainData.lastVisits.length ||
      0,
    icon: 'i-lucide-calendar-check',
    loading:
      requestsState.loadingVisitorInfo ||
      requestsState.loadingVisitHistory ||
      requestsState.loadingLastVisits,
  },
  {
    key: 'next',
    label: 'Próximas visitas',
    value: domainData.nextVisitsWithMe.length,
    icon: 'i-lucide-calendar-clock',
    loading: requestsState.loadingNextVisitsWithMe,
  },
  {
    key: 'offers',
    label: 'Propostas em andamento',
    value: visitorOffersInProgressCount.value,
    icon: 'i-lucide-file-signature',
    loading:
      requestsState.loadingVisitorInfo || requestsState.loadingOngoingOffers,
  },
  {
    key: 'history',
    label: 'Histórico detalhado',
    value: domainData.visitHistory.length || domainData.lastVisits.length,
    icon: 'i-lucide-history',
    loading:
      requestsState.loadingVisitHistory || requestsState.loadingLastVisits,
  },
]);
const visitorProfileNotes = computed(() => {
  const attrs =
    visitorInfo.value.additionalAttributes ||
    visitorInfo.value.additional_attributes ||
    {};
  return [
    visitorInfo.value.notes,
    visitorInfo.value.observations,
    visitorInfo.value.observacoes,
    attrs.notes,
    attrs.observations,
    attrs.observacoes,
  ].find(value => value);
});
const visitorNotes = computed(() => {
  if (!props.visitorId) return [];
  return notesByContact.value(props.visitorId) || [];
});
const visibleVisitorNotes = computed(() =>
  visitorNotes.value.slice(0, notesVisibleLimit.value)
);
const hasMoreVisitorNotes = computed(
  () => visitorNotes.value.length > visibleVisitorNotes.value.length
);
const visibleFavoriteHouses = computed(() =>
  domainData.favoriteHouses.slice(0, favoriteHousesVisibleLimit.value)
);
const visibleVisitedHouses = computed(() =>
  domainData.visitedHouses.slice(0, visitedHousesVisibleLimit.value)
);
const visibleSuggestedHouses = computed(() =>
  domainData.suggestedHouses.slice(0, suggestedHousesVisibleLimit.value)
);
const hasMoreFavoriteHouses = computed(
  () => domainData.favoriteHouses.length > visibleFavoriteHouses.value.length
);
const hasMoreVisitedHouses = computed(
  () => domainData.visitedHouses.length > visibleVisitedHouses.value.length
);
const hasMoreSuggestedHouses = computed(
  () => domainData.suggestedHouses.length > visibleSuggestedHouses.value.length
);
const hasAnyHouse = computed(
  () =>
    Boolean(domainData.favoriteHouses.length) ||
    Boolean(domainData.visitedHouses.length) ||
    Boolean(domainData.suggestedHouses.length)
);
const isLoadingHousesTab = computed(
  () => requestsState.loadingHouses || requestsState.loadingSuggestedHouses
);
const historyItemsForSummary = computed(() =>
  domainData.visitHistory.length
    ? domainData.visitHistory
    : domainData.lastVisits
);
const offerLinkSent = visit =>
  Boolean(visit?.offer_link_sent || visit?.offerLinkSent);
const operationalHistoryRows = computed(() => {
  const items = historyItemsForSummary.value;
  const latest = items[0];
  return [
    {
      key: 'loaded',
      label: 'Registros carregados',
      value: items.length,
    },
    {
      key: 'agent',
      label: 'Visitas com você',
      value: items.filter(visit => isAgentVisit(visit)).length,
    },
    {
      key: 'offers',
      label: 'Links de proposta enviados',
      value: items.filter(visit => offerLinkSent(visit)).length,
    },
    {
      key: 'latest',
      label: 'Último evento',
      value: latest ? visitDateTimeLabel(latest) : '',
    },
  ].filter(row => row.value !== '');
});
const operationalStatusRows = computed(() => {
  const statuses = historyItemsForSummary.value.reduce((acc, visit) => {
    const label =
      visit?.status_label ||
      visit?.statusLabel ||
      visit?.status ||
      'Sem status';
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(statuses).map(([label, count]) => ({
    label,
    count,
    displayLabel: `${label}: ${count}`,
  }));
});
const clientProfileTabs = computed(() => [
  {
    id: CLIENT_PROFILE_TABS.OVERVIEW,
    label: VISITOR_PROFILE_EXTRA_COPY.tabOverview,
    icon: 'i-lucide-layout-dashboard',
  },
  {
    id: CLIENT_PROFILE_TABS.HOUSES,
    label: VISITOR_PROFILE_EXTRA_COPY.tabHouses,
    count:
      domainData.favoriteHouses.length +
      domainData.visitedHouses.length +
      domainData.suggestedHouses.length,
    icon: 'i-lucide-home',
  },
  {
    id: CLIENT_PROFILE_TABS.NOTES,
    label: VISITOR_PROFILE_EXTRA_COPY.tabNotes,
    count: visitorNotes.value.length,
    icon: 'i-lucide-notebook-pen',
  },
  {
    id: CLIENT_PROFILE_TABS.HISTORY,
    label: VISITOR_PROFILE_EXTRA_COPY.tabHistory,
    count: domainData.visitHistory.length || domainData.lastVisits.length,
    icon: 'i-lucide-history',
  },
]);
const clientProfileTabItems = computed(() =>
  clientProfileTabs.value.map(tab => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    badge: tab.count,
  }))
);
const isFetchingVisitorNotes = computed(
  () => contactNotesUiFlags.value?.isFetching
);
const isCreatingVisitorNote = computed(
  () => contactNotesUiFlags.value?.isCreating
);
const trimmedNoteDraft = computed(() => noteDraft.value.trim());
const noteDraftCount = computed(() => trimmedNoteDraft.value.length);
const noteLimitCounterLabel = computed(
  () =>
    `${noteDraftCount.value}/255 · ${VISITOR_PROFILE_EXTRA_COPY.noteLimitHelper}`
);
const visitorOffersSummary = computed(() => {
  const count = visitorOffersInProgressCount.value;
  if (count === 1) return '1 proposta em andamento para este visitante.';
  return `${count} propostas em andamento para este visitante.`;
});
const hasMoreVisitHistory = computed(
  () =>
    Boolean(domainData.historyMeta?.hasMore) ||
    Boolean(domainData.historyMeta?.nextPage)
);
const normalizedActionContext = computed(() =>
  Object.entries(props.actionContext || {}).reduce((context, [key, value]) => {
    if (value === undefined || value === null || value === '') return context;
    context[key] = String(value);
    return context;
  }, {})
);

const withActionContext = (path, extraContext = {}) => {
  if (!path) return '';

  const [basePath, queryString = ''] = path.split('?');
  const params = new URLSearchParams(queryString);
  Object.entries({
    ...normalizedActionContext.value,
    ...extraContext,
  }).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (!params.has(key)) params.set(key, String(value));
  });

  const query = params.toString();
  return `${basePath}${query ? `?${query}` : ''}`;
};

const trackVisitorEvent = (name, properties = {}) =>
  trackCrmEvent({
    accountId: accountId.value,
    name,
    source: 'dashboard_doc33',
    properties: {
      agent_id: currentUser.value?.id,
      visitor_id: props.visitorId,
      ...properties,
    },
  });

const trackVisitorParityEvent = (name, properties = {}) =>
  trackCrmEvent({
    accountId: accountId.value,
    name,
    source: 'quinto_parity',
    properties: {
      agent_id: currentUser.value?.id,
      visitor_id: props.visitorId,
      ...properties,
    },
  });

// LaisTabs owns the active-id state via v-model and only emits `change` on a
// real change, so this handler is purely the parity tracking side-effect.
const handleClientProfileTabChange = tabId => {
  trackVisitorParityEvent('crm_cp_tab_change', { option: tabId });
};

const localErrorKind = error => {
  const status = Number(
    error?.response?.status || error?.status || error?.request?.status
  );
  if (status === 403 || String(error?.message || error).includes('403')) {
    return 'permission';
  }
  return 'notes';
};

async function loadVisitorNotes(visitorId) {
  if (!visitorId) return;

  notesError.value = '';

  try {
    await store.dispatch('contactNotes/get', { contactId: visitorId });
  } catch (error) {
    notesError.value = localErrorKind(error);
    useAlert(VISITOR_PROFILE_EXTRA_COPY.notesFetchError);
  }
}

const resetClientProfileUi = () => {
  selectedEntryHouse.value = null;
  entryInformation.value = null;
  isLoadingEntryInformation.value = false;
  isNewNoteDialogOpen.value = false;
  isDiscardNoteDialogOpen.value = false;
  currentClientProfileTab.value = CLIENT_PROFILE_TABS.OVERVIEW;
  noteDraft.value = '';
  favoriteHousesVisibleLimit.value = VISITOR_PROFILE_PAGE_SIZE;
  visitedHousesVisibleLimit.value = VISITOR_PROFILE_PAGE_SIZE;
  suggestedHousesVisibleLimit.value = VISITOR_PROFILE_PAGE_SIZE;
  offersVisibleLimit.value = VISITOR_PROFILE_PAGE_SIZE;
  notesVisibleLimit.value = VISITOR_NOTES_PAGE_SIZE;
  notesError.value = '';
};

const reset = () => {
  resetClientProfileUi();
  resetDomainData({ invalidate: true });
};

watch(
  () => [props.visitorId, activeBusinessContext.value],
  ([visitorId, businessContext]) => {
    resetClientProfileUi();
    load(visitorId, businessContext);
    loadVisitorNotes(visitorId);
    if (visitorId) {
      trackVisitorParityEvent('visitorinfo_page_viewed', {
        business_context: businessContext,
      });
    }
  },
  { immediate: true }
);

const openNewNoteDialog = () => {
  noteDraft.value = '';
  isNewNoteDialogOpen.value = true;
  trackVisitorParityEvent('crm_cp_new_note_clicked');
};

const resetNewNoteDialog = () => {
  noteDraft.value = '';
  isDiscardNoteDialogOpen.value = false;
  isNewNoteDialogOpen.value = false;
};

const closeNewNoteDialog = () => {
  if (trimmedNoteDraft.value) {
    isDiscardNoteDialogOpen.value = true;
    return;
  }

  resetNewNoteDialog();
};

const saveVisitorNote = async () => {
  if (
    !props.visitorId ||
    !trimmedNoteDraft.value ||
    isCreatingVisitorNote.value
  )
    return;

  try {
    await store.dispatch('contactNotes/create', {
      contactId: props.visitorId,
      content: trimmedNoteDraft.value,
    });
    notesVisibleLimit.value = Math.max(
      notesVisibleLimit.value,
      VISITOR_NOTES_PAGE_SIZE
    );
    notesError.value = '';
    useAlert(VISITOR_PROFILE_EXTRA_COPY.createNoteSuccess);
    trackVisitorParityEvent('crm_cp_note_created');
    resetNewNoteDialog();
  } catch {
    useAlert(VISITOR_PROFILE_EXTRA_COPY.createNoteError);
  }
};

const normalizeVisitorNoteDate = value => {
  if (typeof value === 'number') {
    return value < 100000000000 ? value * 1000 : value;
  }

  const stringValue = String(value);
  if (/^\d+$/.test(stringValue)) {
    const timestamp = Number(stringValue);
    return timestamp < 100000000000 ? timestamp * 1000 : timestamp;
  }

  return value;
};

const formatVisitorNoteDate = note => {
  const rawDate =
    note.createdAt || note.created_at || note.updatedAt || note.updated_at;
  if (!rawDate) return '';

  const date = new Date(normalizeVisitorNoteDate(rawDate));
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const visitorNoteContent = note =>
  note.content || note.notes || note.comment || '';

const visitorNoteAuthor = note => {
  const user = note.user || {};
  if (user.id && user.id === currentUser.value?.id) return 'Você';
  return user.name || note.userName || 'Bot';
};

const openPhoneDialer = () => {
  const phone = visitorPhone(visitorInfo.value);
  if (!phone) {
    useAlert(t('LEADS.VISITOR_INFO.COMMUNICATION.NO_PHONE_ALERT'));
    return;
  }

  window.location.href = `tel:${phone}`;
};

const handlePhone = payload => {
  trackVisitorEvent('VISITOR_INFO_PHONE_CLICKED', { channel: 'phone' });
  trackVisitorParityEvent('visitorinfo_phone_clicked', { channel: 'phone' });
  emit('phone', {
    ...payload,
    visitorId: props.visitorId,
    isPhoneDialer: true,
  });
  openPhoneDialer();
};

const handleChat = payload => {
  trackVisitorEvent('VISITOR_INFO_WHATSAPP_CLICKED', { channel: 'chatwoot' });
  trackVisitorParityEvent('visitorinfo_whatsapp_clicked', {
    channel: 'chatwoot',
  });
  trackVisitorParityEvent('chatcrpp_access_chat_clicked', {
    channel: 'chatwoot',
  });
  emit('chat', {
    ...payload,
    visitorId: props.visitorId,
  });

  const conversationId =
    visitorInfo.value.latest_conversation_id ||
    visitorInfo.value.latestConversationId;
  if (conversationId) {
    router.push(
      withActionContext(
        frontendURL(
          `accounts/${accountId.value}/conversations/${conversationId}`
        )
      )
    );
    return;
  }

  useAlert(t('LEADS.VISITOR_INFO.COMMUNICATION.NO_CHAT_ALERT'));
};

const handleOpenVisit = visit => {
  emit('openVisit', visit);
  const path = visitDetailsUrl(accountId.value, visit?.id || visit?.visit_id);
  if (path) router.push(withActionContext(path));
};

const handleScheduleVisit = () => {
  emit('scheduleVisit', props.visitorId);
  router.push(
    withActionContext(
      frontendURL(`accounts/${accountId.value}/visitas/new`, {
        contact_id: String(props.visitorId),
      })
    )
  );
};

const handleOpenOffers = () => {
  router.push(
    withActionContext(
      frontendURL(`accounts/${accountId.value}/propostas`, {
        contact_id: String(props.visitorId),
      })
    )
  );
};

const handleOpenOffer = offer => {
  const path = visitorOfferDetailUrl({
    accountId: accountId.value,
    offer,
    visitorId: props.visitorId,
  });
  if (!path) {
    handleOpenOffers();
    return;
  }

  trackVisitorParityEvent('visitorinfo_ongoing_offer_clicked', {
    offer_id: visitorOfferReference(offer),
    house_id: houseId(visitorOfferHouse(offer)),
  });
  router.push(withActionContext(path));
};

const handleViewListing = house => {
  emit('viewListing', house);
  const path = listingUrl(accountId.value, houseId(house));
  if (path) router.push(withActionContext(path));
};

const handleEntryInfo = async house => {
  selectedEntryHouse.value = house;
  entryInformation.value = house?.entry_information || house?.entryInformation;

  const id = houseId(house);
  if (!id) return;
  trackVisitorParityEvent('entrance_info_property_viewed', {
    house_id: id,
  });

  isLoadingEntryInformation.value = true;
  try {
    const response = await ImovelAPI.entryInformation(id);
    entryInformation.value = response?.data?.payload || entryInformation.value;
  } catch {
    useAlert(t('LEADS.VISITOR_INFO.ENTRY.ERROR'));
  } finally {
    isLoadingEntryInformation.value = false;
  }
};

const closeEntryInfo = () => {
  selectedEntryHouse.value = null;
  entryInformation.value = null;
};

const markOfferLinkSent = visitId => {
  [domainData.lastVisits, domainData.visitHistory].forEach(collection => {
    const sentVisit = collection.find(item => item.id === visitId);
    if (sentVisit) {
      sentVisit.offer_link_sent = true;
      sentVisit.offerLinkSent = true;
    }
  });
};

const handleSendOffer = async visit => {
  const house = visitHouse(visit);
  const path = offerDeepLinkUrl({
    accountId: accountId.value,
    visitorId: props.visitorId,
    houseId: houseId(house),
  });
  if (!path) {
    useAlert(t('LEADS.VISITOR_INFO.LAST_VISITS.SEND_OFFER_ERROR'));
    return;
  }

  try {
    await PropertyOffersAPI.sendOfferLink({
      imovel_id: houseId(house),
      contact_id: props.visitorId,
    });
    markOfferLinkSent(visit.id);
  } catch {
    useAlert(t('LEADS.VISITOR_INFO.LAST_VISITS.SEND_OFFER_ERROR'));
    return;
  }

  trackVisitorEvent('VISITOR_INFO_LAST_VISITS_CARD_SEND_OFFER_CLICKED', {
    channel: 'chatwoot',
    pageName: 'VISITOR_INFO',
    house_id: houseId(house),
  });
  trackVisitorParityEvent('visitorinfo_last_visits_card_send_offer_clicked', {
    channel: 'chatwoot',
    house_id: houseId(house),
  });
  emit('sendOffer', visit);
  router.push(withActionContext(path));
};

const handleLoadMoreHistory = () => {
  trackVisitorParityEvent('crm_cp_history_load_more_clicked', {
    page: domainData.historyMeta?.nextPage,
  });
  loadMoreVisitHistory();
};

const incrementLocalList = (
  limit,
  eventName,
  properties = {},
  pageSize = VISITOR_PROFILE_PAGE_SIZE
) => {
  limit.value += pageSize;
  trackVisitorParityEvent(eventName, properties);
};

const handleLoadMoreFavoriteHouses = () =>
  incrementLocalList(favoriteHousesVisibleLimit, 'crm_cp_houses_load_more', {
    list: 'favorites',
  });

const handleLoadMoreVisitedHouses = () =>
  incrementLocalList(visitedHousesVisibleLimit, 'crm_cp_houses_load_more', {
    list: 'visited',
  });

const handleLoadMoreSuggestedHouses = () =>
  incrementLocalList(suggestedHousesVisibleLimit, 'crm_cp_houses_load_more', {
    list: 'suggested',
  });

const handleLoadMoreOffers = () =>
  incrementLocalList(offersVisibleLimit, 'crm_cp_offers_load_more');

const handleLoadMoreNotes = () =>
  incrementLocalList(
    notesVisibleLimit,
    'crm_cp_notes_load_more',
    {},
    VISITOR_NOTES_PAGE_SIZE
  );

const retryLoad = () => {
  load(props.visitorId, activeBusinessContext.value);
};

onBeforeUnmount(reset);

defineExpose({ reset });
</script>

<template>
  <section
    class="grid gap-4"
    :class="embedded ? '' : 'mx-auto w-full max-w-4xl px-4 py-6 sm:px-6'"
  >
    <div
      v-if="hasTotalError"
      class="grid justify-items-center gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-card px-4 py-8 text-center"
    >
      <span
        class="i-lucide-circle-alert size-8 text-lais-danger"
        aria-hidden="true"
      />
      <div class="grid max-w-md gap-1">
        <h2 class="font-redHatDisplay text-lais-heading-sm text-lais-text">
          {{ t('LEADS.VISITOR_INFO.ERROR_TITLE') }}
        </h2>
        <p class="text-lais-body-sm text-lais-text-muted">
          {{ t('LEADS.VISITOR_INFO.ERROR_DESCRIPTION') }}
        </p>
      </div>
      <button
        type="button"
        class="inline-flex h-10 items-center gap-2 rounded-lais bg-lais-primary px-4 text-lais-label-sm font-semibold text-white transition hover:bg-lais-primary-600"
        @click="retryLoad"
      >
        <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
        {{ t('LEADS.VISITOR_INFO.RETRY') }}
      </button>
    </div>

    <template v-else>
      <VisitorProfileHeader
        :visitor-info="visitorInfo"
        :lead="lead"
        :loading="requestsState.loadingVisitorInfo"
        @phone="handlePhone"
        @chat="handleChat"
      />

      <p
        v-if="hasPartialError"
        class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-lais-body-sm text-lais-text"
      >
        {{ t('LEADS.VISITOR_INFO.PARTIAL_ERROR') }}
      </p>

      <LaisTabs
        v-model="currentClientProfileTab"
        :tabs="clientProfileTabItems"
        :aria-label="VISITOR_PROFILE_EXTRA_COPY.clientProfileTabsLabel"
        @change="handleClientProfileTabChange"
      >
        <template #overview>
          <div class="grid gap-4">
            <p
              v-if="summaryStateMessage"
              class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-lais-body-sm text-lais-text"
            >
              {{ summaryStateMessage }}
            </p>

            <VisitorInfoSection
              :visitor-info="visitorInfo"
              :business-context="activeBusinessContext"
              :loading="requestsState.loadingVisitorInfo"
            />

            <LaisCard
              :title="VISITOR_PROFILE_EXTRA_COPY.summaryTitle"
              prefix-icon="i-lucide-user-round-check"
            >
              <dl v-if="summaryRows.length" class="grid gap-3 sm:grid-cols-2">
                <div
                  v-for="row in summaryRows"
                  :key="row.key"
                  class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3"
                >
                  <dt
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    {{ row.label }}
                  </dt>
                  <dd class="mt-1 text-[14px] font-semibold text-lais-text">
                    {{ row.value }}
                  </dd>
                </div>
              </dl>
              <div
                v-else
                class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
              >
                {{ VISITOR_PROFILE_EXTRA_COPY.summaryEmpty }}
              </div>
            </LaisCard>

            <LaisCard
              :title="VISITOR_PROFILE_EXTRA_COPY.preferencesTitle"
              prefix-icon="i-lucide-sliders-horizontal"
            >
              <dl
                v-if="preferenceRows.length"
                class="grid gap-3 sm:grid-cols-2"
              >
                <div
                  v-for="row in preferenceRows"
                  :key="row.key"
                  class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-3"
                >
                  <dt
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    {{ row.label }}
                  </dt>
                  <dd class="mt-1 text-[14px] font-semibold text-lais-text">
                    {{ row.value }}
                  </dd>
                </div>
              </dl>
              <div
                v-else
                class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
              >
                {{ VISITOR_PROFILE_EXTRA_COPY.preferencesEmpty }}
              </div>
            </LaisCard>

            <LaisCard
              :title="VISITOR_PROFILE_EXTRA_COPY.journeyTitle"
              prefix-icon="i-lucide-route"
            >
              <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div
                  v-for="card in visitorJourneyCards"
                  :key="card.key"
                  class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3"
                >
                  <span
                    class="grid size-8 place-items-center rounded-full bg-white text-lais-primary"
                  >
                    <span
                      class="size-4"
                      :class="[card.icon]"
                      aria-hidden="true"
                    />
                  </span>
                  <p
                    v-if="card.loading"
                    class="mt-3 h-6 w-12 animate-pulse rounded bg-lais-bg-muted"
                  />
                  <p
                    v-else
                    class="mt-3 text-[20px] font-semibold text-lais-text"
                  >
                    {{ card.value }}
                  </p>
                  <p
                    class="mt-0.5 text-[12px] font-semibold text-lais-text-muted"
                  >
                    {{ card.label }}
                  </p>
                </div>
              </div>
            </LaisCard>

            <LaisCard
              :title="VISITOR_PROFILE_EXTRA_COPY.offersTitle"
              prefix-icon="i-lucide-file-signature"
            >
              <div class="grid gap-4">
                <p
                  v-if="offersStateMessage"
                  class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-lais-body-sm text-lais-text"
                >
                  {{ offersStateMessage }}
                </p>

                <div class="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                  <div>
                    <p class="text-[14px] leading-relaxed text-lais-text-body">
                      {{ visitorOffersSummary }}
                    </p>
                    <p
                      class="mt-1 text-[13px] leading-relaxed text-lais-text-muted"
                    >
                      {{ VISITOR_PROFILE_EXTRA_COPY.offersHelper }}
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-4 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
                      @click="handleOpenOffers"
                    >
                      <span
                        class="i-lucide-file-search size-4"
                        aria-hidden="true"
                      />
                      {{ VISITOR_PROFILE_EXTRA_COPY.viewOffers }}
                    </button>
                    <button
                      type="button"
                      class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-4 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
                      @click="handleScheduleVisit"
                    >
                      <span
                        class="i-lucide-calendar-plus size-4"
                        aria-hidden="true"
                      />
                      {{ VISITOR_PROFILE_EXTRA_COPY.newVisit }}
                    </button>
                  </div>
                </div>

                <div
                  v-if="requestsState.loadingOngoingOffers"
                  class="grid gap-2"
                  :aria-label="VISITOR_PROFILE_EXTRA_COPY.offersLoading"
                >
                  <span
                    v-for="item in 2"
                    :key="item"
                    class="h-[74px] animate-pulse rounded-lais-sm bg-lais-bg-muted"
                  />
                </div>

                <article
                  v-if="
                    !requestsState.loadingOngoingOffers && activeVisitorOffer
                  "
                  class="rounded-lais-sm border border-lais-primary/20 bg-lais-primary-50 px-3 py-3"
                >
                  <div
                    class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                  >
                    <div class="min-w-0">
                      <p
                        class="text-[11px] font-semibold uppercase text-lais-primary"
                      >
                        {{ VISITOR_PROFILE_EXTRA_COPY.activeOfferTitle }}
                      </p>
                      <p class="mt-1 font-semibold text-lais-text">
                        {{
                          houseAddressLine(activeVisitorOfferHouse) ||
                          VISITOR_PROFILE_EXTRA_COPY.offerFallbackHouse
                        }}
                      </p>
                      <p
                        v-if="activeVisitorOfferHouseDescription"
                        class="mt-1 text-[13px] text-lais-text-muted"
                      >
                        {{ activeVisitorOfferHouseDescription }}
                      </p>
                      <p class="mt-1 text-[13px] text-lais-text-muted">
                        {{ VISITOR_PROFILE_EXTRA_COPY.offerReferencePrefix }}
                        {{ visitorOfferReference(activeVisitorOffer) }}
                        {{ VISITOR_PROFILE_EXTRA_COPY.offerSeparator }}
                        {{ visitorOfferStatusLabel(activeVisitorOffer) }}
                      </p>
                    </div>
                    <div class="flex flex-wrap gap-2 sm:justify-end">
                      <button
                        type="button"
                        class="inline-flex h-9 items-center justify-center gap-2 rounded-lais bg-lais-primary px-3 text-[13px] font-semibold text-white transition hover:bg-lais-primary-600"
                        @click="handleOpenOffer(activeVisitorOffer)"
                      >
                        <span
                          class="i-lucide-file-search size-4"
                          aria-hidden="true"
                        />
                        {{ VISITOR_PROFILE_EXTRA_COPY.activeOfferOpen }}
                      </button>
                      <button
                        v-if="houseId(activeVisitorOfferHouse)"
                        type="button"
                        class="inline-flex h-9 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
                        @click="handleViewListing(activeVisitorOfferHouse)"
                      >
                        <span class="i-lucide-home size-4" aria-hidden="true" />
                        {{ VISITOR_PROFILE_EXTRA_COPY.activeOfferListing }}
                      </button>
                    </div>
                  </div>
                </article>

                <div>
                  <button
                    v-for="offer in visibleVisitorOffersInProgress"
                    v-show="!requestsState.loadingOngoingOffers"
                    :key="visitorOfferReference(offer)"
                    type="button"
                    class="grid w-full gap-2 border-t border-lais-border-soft px-0 py-3 text-left transition hover:bg-lais-primary-50 sm:grid-cols-[1fr_auto] sm:items-center sm:px-3"
                    @click="handleOpenOffer(offer)"
                  >
                    <span class="grid min-w-0 gap-1">
                      <span
                        class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
                      >
                        <span
                          class="truncate text-[14px] font-semibold text-lais-text"
                        >
                          {{
                            houseAddressLine(visitorOfferHouse(offer)) ||
                            VISITOR_PROFILE_EXTRA_COPY.offerFallbackHouse
                          }}
                        </span>
                        <span
                          class="rounded-full bg-lais-primary-50 px-2 py-0.5 text-[11px] font-semibold text-lais-primary"
                        >
                          {{ visitorOfferStatusLabel(offer) }}
                        </span>
                      </span>
                      <span class="text-[13px] text-lais-text-muted">
                        {{ VISITOR_PROFILE_EXTRA_COPY.offerReferencePrefix }}
                        {{ visitorOfferReference(offer) }}
                        <template v-if="visitorOfferSubmittedAtLabel(offer)">
                          {{ VISITOR_PROFILE_EXTRA_COPY.offerSeparator }}
                          {{ VISITOR_PROFILE_EXTRA_COPY.offerSentAtPrefix }}
                          {{ visitorOfferSubmittedAtLabel(offer) }}
                        </template>
                      </span>
                    </span>
                    <span
                      class="flex items-center justify-between gap-3 sm:justify-end"
                    >
                      <span class="text-right">
                        <span
                          class="block text-[13px] font-semibold text-lais-text"
                        >
                          {{
                            visitorOfferAmountLabel(offer) ||
                            VISITOR_PROFILE_EXTRA_COPY.offerFallbackAmount
                          }}
                        </span>
                        <span
                          v-if="visitorOfferPaymentLabel(offer)"
                          class="block text-[12px] text-lais-text-muted"
                        >
                          {{ visitorOfferPaymentLabel(offer) }}
                        </span>
                      </span>
                      <span
                        class="i-lucide-chevron-right size-4 shrink-0 text-lais-text-muted"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </div>

                <button
                  v-if="hasMoreVisitorOffers"
                  type="button"
                  class="mx-auto inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft bg-white px-4 text-lais-label-sm font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
                  @click="handleLoadMoreOffers"
                >
                  <span
                    class="i-lucide-chevron-down size-4"
                    aria-hidden="true"
                  />
                  {{ VISITOR_PROFILE_EXTRA_COPY.offersLoadMore }}
                </button>

                <div
                  v-if="
                    !requestsState.loadingOngoingOffers &&
                    !visitorOffersInProgress.length &&
                    !offersStateMessage
                  "
                  class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
                >
                  {{ VISITOR_PROFILE_EXTRA_COPY.offersEmpty }}
                </div>
              </div>
            </LaisCard>

            <VisitListCard
              variant="next"
              :visits="domainData.nextVisitsWithMe"
              :loading="requestsState.loadingNextVisitsWithMe"
              :title="t('LEADS.VISITOR_INFO.NEXT_VISITS.TITLE')"
              icon="i-lucide-calendar-clock"
              :empty-title="t('LEADS.VISITOR_INFO.NEXT_VISITS.EMPTY_TITLE')"
              :empty-description="
                t('LEADS.VISITOR_INFO.NEXT_VISITS.EMPTY_DESCRIPTION')
              "
              @open-visit="handleOpenVisit"
              @schedule="handleScheduleVisit"
            />
          </div>
        </template>

        <template #houses>
          <div class="grid gap-4">
            <p
              v-if="housesStateMessage"
              class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-lais-body-sm text-lais-text"
            >
              {{ housesStateMessage }}
            </p>
            <VisitorProfileHouseSection
              :title="VISITOR_PROFILE_EXTRA_COPY.favoriteHousesTitle"
              :empty-text="VISITOR_PROFILE_EXTRA_COPY.favoriteHousesEmpty"
              :items="visibleFavoriteHouses"
              :loading="requestsState.loadingHouses"
              icon="i-lucide-heart"
              :meta-prefix="VISITOR_PROFILE_EXTRA_COPY.favoritedAtPrefix"
              :action-label="VISITOR_PROFILE_EXTRA_COPY.viewListingAction"
              @entry-info="handleEntryInfo"
              @view-listing="handleViewListing"
            />
            <button
              v-if="hasMoreFavoriteHouses"
              type="button"
              class="mx-auto inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft bg-white px-4 text-lais-label-sm font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
              @click="handleLoadMoreFavoriteHouses"
            >
              <span class="i-lucide-chevron-down size-4" aria-hidden="true" />
              {{ VISITOR_PROFILE_EXTRA_COPY.loadMore }}
            </button>
            <VisitorProfileHouseSection
              :title="VISITOR_PROFILE_EXTRA_COPY.visitedHousesTitle"
              :empty-text="VISITOR_PROFILE_EXTRA_COPY.visitedHousesEmpty"
              :items="visibleVisitedHouses"
              :loading="requestsState.loadingHouses"
              icon="i-lucide-map-pinned"
              :meta-prefix="VISITOR_PROFILE_EXTRA_COPY.visitedAtPrefix"
              :action-label="VISITOR_PROFILE_EXTRA_COPY.viewListingAction"
              @entry-info="handleEntryInfo"
              @view-listing="handleViewListing"
            />
            <button
              v-if="hasMoreVisitedHouses"
              type="button"
              class="mx-auto inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft bg-white px-4 text-lais-label-sm font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
              @click="handleLoadMoreVisitedHouses"
            >
              <span class="i-lucide-chevron-down size-4" aria-hidden="true" />
              {{ VISITOR_PROFILE_EXTRA_COPY.loadMore }}
            </button>
            <VisitorSuggestedHouses
              :houses="visibleSuggestedHouses"
              :visitor-info="visitorInfo"
              :business-context="activeBusinessContext"
              :loading="requestsState.loadingSuggestedHouses"
              @entry-info="handleEntryInfo"
              @view-listing="handleViewListing"
            />
            <button
              v-if="hasMoreSuggestedHouses"
              type="button"
              class="mx-auto inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft bg-white px-4 text-lais-label-sm font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
              @click="handleLoadMoreSuggestedHouses"
            >
              <span class="i-lucide-chevron-down size-4" aria-hidden="true" />
              {{ VISITOR_PROFILE_EXTRA_COPY.loadMore }}
            </button>
            <LaisCard
              v-if="!isLoadingHousesTab && !hasAnyHouse && !housesStateMessage"
              :title="VISITOR_PROFILE_EXTRA_COPY.housesTitle"
              prefix-icon="i-lucide-home"
            >
              <div
                class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
              >
                {{ VISITOR_PROFILE_EXTRA_COPY.housesEmpty }}
              </div>
              <button
                type="button"
                class="mt-3 inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-4 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
                @click="handleScheduleVisit"
              >
                <span
                  class="i-lucide-calendar-plus size-4"
                  aria-hidden="true"
                />
                {{ VISITOR_PROFILE_EXTRA_COPY.housesAction }}
              </button>
            </LaisCard>
          </div>
        </template>

        <template #notes>
          <div class="grid gap-4">
            <LaisCard
              :title="VISITOR_PROFILE_EXTRA_COPY.notesCardTitle"
              prefix-icon="i-lucide-notebook-pen"
            >
              <template #titleAction>
                <button
                  type="button"
                  class="inline-flex h-9 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="
                    isFetchingVisitorNotes || notesError === 'permission'
                  "
                  @click="openNewNoteDialog"
                >
                  <span class="i-lucide-plus size-4" aria-hidden="true" />
                  {{ VISITOR_PROFILE_EXTRA_COPY.newNote }}
                </button>
              </template>

              <div class="grid gap-3">
                <p
                  v-if="notesErrorMessage"
                  class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-lais-body-sm text-lais-text"
                >
                  {{ notesErrorMessage }}
                </p>
                <p class="text-[14px] leading-relaxed text-lais-text-body">
                  {{ VISITOR_PROFILE_EXTRA_COPY.notesDescription }}
                </p>
                <p
                  v-if="visitorNotes.length"
                  class="text-[12px] font-semibold uppercase text-lais-text-muted"
                >
                  {{ VISITOR_PROFILE_EXTRA_COPY.noteCreationOrder }}
                </p>

                <div v-if="isFetchingVisitorNotes" class="grid gap-2">
                  <span
                    v-for="item in 2"
                    :key="item"
                    class="h-[76px] animate-pulse rounded-lais-sm bg-lais-bg-muted"
                  />
                </div>

                <div v-else-if="visibleVisitorNotes.length" class="grid gap-3">
                  <article
                    v-for="note in visibleVisitorNotes"
                    :key="note.id"
                    class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-3"
                  >
                    <div class="flex flex-wrap items-center gap-2 text-[12px]">
                      <span class="font-semibold text-lais-text">
                        {{ visitorNoteAuthor(note) }}
                      </span>
                      <span
                        v-if="formatVisitorNoteDate(note)"
                        class="text-lais-text-muted"
                      >
                        {{ formatVisitorNoteDate(note) }}
                      </span>
                    </div>
                    <p
                      class="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed text-lais-text-body"
                    >
                      {{ visitorNoteContent(note) }}
                    </p>
                  </article>
                </div>

                <button
                  v-if="hasMoreVisitorNotes"
                  type="button"
                  class="mx-auto inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft bg-white px-4 text-lais-label-sm font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
                  @click="handleLoadMoreNotes"
                >
                  <span
                    class="i-lucide-chevron-down size-4"
                    aria-hidden="true"
                  />
                  {{ VISITOR_PROFILE_EXTRA_COPY.notesLoadMore }}
                </button>

                <div
                  v-else-if="!notesErrorMessage"
                  class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
                >
                  {{
                    visitorProfileNotes ||
                    VISITOR_PROFILE_EXTRA_COPY.noteEmptyState
                  }}
                </div>
              </div>
            </LaisCard>
          </div>
        </template>

        <template #history>
          <div class="grid gap-4">
            <p
              v-if="historyStateMessage"
              class="rounded-lais-sm border border-lais-warning/30 bg-lais-warning/10 px-3 py-2 text-lais-body-sm text-lais-text"
            >
              {{ historyStateMessage }}
            </p>

            <LaisCard
              :title="VISITOR_PROFILE_EXTRA_COPY.operationalHistoryTitle"
              prefix-icon="i-lucide-activity"
            >
              <div
                v-if="requestsState.loadingVisitHistory"
                class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
              >
                <span
                  v-for="item in 4"
                  :key="item"
                  class="h-20 animate-pulse rounded-lais-sm bg-lais-bg-muted"
                />
              </div>
              <div v-else class="grid gap-4">
                <p class="text-[13px] leading-relaxed text-lais-text-muted">
                  {{ VISITOR_PROFILE_EXTRA_COPY.operationalHistoryHelper }}
                </p>
                <dl class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div
                    v-for="row in operationalHistoryRows"
                    :key="row.key"
                    class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3"
                  >
                    <dt
                      class="text-[11px] font-semibold uppercase text-lais-text-muted"
                    >
                      {{ row.label }}
                    </dt>
                    <dd class="mt-1 text-[18px] font-semibold text-lais-text">
                      {{ row.value }}
                    </dd>
                  </div>
                </dl>
                <div
                  v-if="operationalStatusRows.length"
                  class="flex flex-wrap gap-2"
                >
                  <span
                    v-for="row in operationalStatusRows"
                    :key="row.label"
                    class="rounded-full border border-lais-border-soft bg-white px-2.5 py-1 text-[12px] font-semibold text-lais-text-muted"
                  >
                    {{ row.displayLabel }}
                  </span>
                </div>
              </div>
            </LaisCard>

            <VisitListCard
              variant="last"
              :visits="domainData.visitHistory"
              :business-context="activeBusinessContext"
              :loading="requestsState.loadingVisitHistory"
              :title="VISITOR_PROFILE_EXTRA_COPY.detailedHistoryTitle"
              icon="i-lucide-history"
              @open-visit="handleOpenVisit"
              @send-offer="handleSendOffer"
              @view-listing="handleViewListing"
            />
            <button
              v-if="hasMoreVisitHistory && domainData.visitHistory.length"
              type="button"
              class="mx-auto inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft bg-white px-4 text-lais-label-sm font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="requestsState.loadingMoreVisitHistory"
              @click="handleLoadMoreHistory"
            >
              <span
                class="size-4"
                :class="
                  requestsState.loadingMoreVisitHistory
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-chevron-down'
                "
                aria-hidden="true"
              />
              {{
                requestsState.loadingMoreVisitHistory
                  ? VISITOR_PROFILE_EXTRA_COPY.historyLoadingMore
                  : VISITOR_PROFILE_EXTRA_COPY.historyLoadMore
              }}
            </button>
            <LaisCard
              v-if="
                !requestsState.loadingVisitHistory &&
                !domainData.visitHistory.length &&
                !historyStateMessage
              "
              :title="VISITOR_PROFILE_EXTRA_COPY.detailedHistoryTitle"
              prefix-icon="i-lucide-history"
            >
              <div
                class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
              >
                {{ VISITOR_PROFILE_EXTRA_COPY.historyEmpty }}
              </div>
            </LaisCard>
          </div>
        </template>
      </LaisTabs>
    </template>

    <LaisModal
      :show="!!selectedEntryHouse"
      :title="t('LEADS.VISITOR_INFO.ENTRY.TITLE')"
      max-width-class="max-w-[520px]"
      @close="closeEntryInfo"
    >
      <div v-if="isLoadingEntryInformation" class="grid gap-3">
        <span class="h-4 w-full animate-pulse rounded bg-lais-bg-muted" />
        <span class="h-4 w-4/5 animate-pulse rounded bg-lais-bg-muted" />
      </div>
      <div v-else class="grid gap-4">
        <p class="text-lais-body-sm text-lais-text-muted">
          {{ houseAddressLine(selectedEntryHouse) }}
        </p>
        <dl v-if="entryRows.length" class="grid gap-3">
          <div
            v-for="row in entryRows"
            :key="row.key"
            class="rounded-lais-sm border border-lais-border-soft px-3 py-2"
          >
            <dt class="text-lais-label-xs uppercase text-lais-text-muted">
              {{ row.label }}
            </dt>
            <dd class="mt-1 text-lais-body-sm font-semibold text-lais-text">
              {{ row.value }}
            </dd>
          </div>
        </dl>
        <p v-else class="text-lais-body-sm text-lais-text-muted">
          {{ t('LEADS.VISITOR_INFO.ENTRY.EMPTY') }}
        </p>
      </div>
    </LaisModal>

    <LaisModal
      :show="isNewNoteDialogOpen"
      :title="VISITOR_PROFILE_EXTRA_COPY.newNoteDialogTitle"
      max-width-class="max-w-[560px]"
      full-screen
      @close="closeNewNoteDialog"
    >
      <div class="grid gap-4">
        <p class="text-[14px] leading-relaxed text-lais-text-body">
          {{ VISITOR_PROFILE_EXTRA_COPY.notesDescription }}
        </p>
        <textarea
          v-model="noteDraft"
          class="min-h-[150px] resize-y rounded-lais border border-lais-border bg-white px-3 py-3 text-[14px] leading-relaxed text-lais-text outline-none transition placeholder:text-lais-text-muted focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50"
          maxlength="255"
          :placeholder="VISITOR_PROFILE_EXTRA_COPY.inputPlaceholder"
        />
        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-[12px] text-lais-text-muted">
            {{ noteLimitCounterLabel }}
          </p>
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[13px] font-semibold text-white transition hover:bg-lais-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="!trimmedNoteDraft || isCreatingVisitorNote"
            @click="saveVisitorNote"
          >
            <span
              v-if="isCreatingVisitorNote"
              class="i-lucide-loader-2 size-4 animate-spin"
              aria-hidden="true"
            />
            <span v-else class="i-lucide-save size-4" aria-hidden="true" />
            {{ VISITOR_PROFILE_EXTRA_COPY.saveNoteButton }}
          </button>
        </div>
      </div>
    </LaisModal>

    <LaisModal
      :show="isDiscardNoteDialogOpen"
      :title="VISITOR_PROFILE_EXTRA_COPY.discardNoteTitle"
      max-width-class="max-w-[420px]"
      @close="isDiscardNoteDialogOpen = false"
    >
      <div class="grid gap-4">
        <p class="text-[14px] leading-relaxed text-lais-text-body">
          {{ VISITOR_PROFILE_EXTRA_COPY.discardNoteDescription }}
        </p>
        <div class="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center rounded-lais border border-lais-border bg-white px-4 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
            @click="isDiscardNoteDialogOpen = false"
          >
            {{ VISITOR_PROFILE_EXTRA_COPY.discardNoteReturn }}
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center rounded-lais bg-lais-danger px-4 text-[13px] font-semibold text-white transition hover:bg-lais-danger/90"
            @click="resetNewNoteDialog"
          >
            {{ VISITOR_PROFILE_EXTRA_COPY.discardNoteCancel }}
          </button>
        </div>
      </div>
    </LaisModal>
  </section>
</template>
